import React from 'react';
import styled from 'styled-components';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';

export const customStyles = {
	control: (base, state) => ({
		...base,
		boxShadow: state.isFocused ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.08)',
		outline: 'none',
		backgroundColor: 'hsl(var(--background))',
		borderColor: state.isDisabled
			? 'hsl(var(--input) / 0.5)'
			: state.selectProps.isInvalid
				? 'hsl(var(--destructive) / 0.67)'
				: state.isFocused
					? 'hsl(var(--primary))'
					: 'hsl(var(--input))',
		borderWidth: 1,
		borderRadius: 'calc(var(--radius) - 2px)',
		fontWeight: '500',
		transition: 'all 0.2s',
		minHeight: '2.75rem',
		opacity: state.isDisabled ? '0.33' : '1',
		':hover': {
			borderColor: state.isDisabled
				? 'hsl(var(--input) / 0.5)'
				: state.isFocused
					? 'hsl(var(--primary))'
					: 'hsl(var(--ring))'
		}
	}),
	singleValue: (base) => ({
		...base,
		color: 'hsl(var(--foreground))'
	}),
	multiValue: (base) => ({
		...base,
		backgroundColor: 'hsl(var(--secondary))',
		border: '1px solid hsl(var(--border))',
		borderRadius: 'calc(var(--radius) - 4px)',
		'&:hover': {
			backgroundColor: 'hsl(var(--accent))'
		}
	}),
	multiValueLabel: (base) => ({
		...base,
		color: 'hsl(var(--foreground))',
		fontWeight: 600
	}),
	multiValueRemove: (base) => ({
		...base,
		color: 'hsl(var(--muted-foreground))',
		'&:hover': {
			color: 'hsl(var(--foreground))',
			backgroundColor: 'hsl(var(--accent))'
		}
	}),
	menu: (base) => ({
		...base,
		backgroundColor: 'hsl(var(--popover))',
		border: '1px solid hsl(var(--border))',
		zIndex: 9999,
		// Radix Dialog locks `body { pointer-events: none }` while open.
		// The portal-ed react-select menu inherits that, which makes
		// elementFromPoint hits return ancestors instead of the menu — and
		// cypress reports it as "covered". Override explicitly.
		pointerEvents: 'auto'
	}),
	menuPortal: (base) => ({
		...base,
		zIndex: 9999,
		pointerEvents: 'auto'
	}),
	menuList: (base) => ({
		...base,
		color: 'hsl(var(--foreground))',
		fontSize: '0.9rem',
		fontWeight: '500'
	}),
	option: (base, state) => ({
		...base,
		backgroundColor: state.isSelected
			? 'hsl(var(--primary))'
			: state.isFocused
				? 'hsl(var(--accent))'
				: 'transparent',
		color: state.isDisabled
			? 'hsl(var(--muted-foreground))'
			: state.isSelected
				? 'hsl(var(--primary-foreground))'
				: 'inherit',
		':active': {
			backgroundColor: !state.isDisabled && (state.isSelected ? 'hsl(var(--primary) / 0.92)' : 'hsl(var(--accent))')
		}
	}),
	indicatorSeparator: (base) => ({
		...base,
		opacity: '0.5'
	}),
	input: (base) => ({
		...base,
		height: 'auto',
		color: 'hsl(var(--foreground))',
		fontWeight: '500'
	}),
	placeholder: (base) => ({
		...base,
		color: 'hsl(var(--muted-foreground))'
	}),
	valueContainer: (base) => ({
		...base,
		lineHeight: '1.5'
	})
};

const DropdownIndicator = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<polyline points="6 9 12 15 18 9" />
			</svg>
		</components.DropdownIndicator>
	);
};

const StyledDropdownIndicator = styled(DropdownIndicator)`
	padding: 0.5rem !important;
	color: hsl(var(--foreground));
`;

// Override default input height for react-select input
const StyledInput = styled(components.Input)`
	input {
		height: auto;
		outline: none !important;
		box-shadow: none !important;
		border: 0 !important;
		background: transparent !important;
		color: hsl(var(--foreground)) !important;
		caret-color: hsl(var(--foreground));

		&::selection {
			background: hsl(var(--primary));
			color: hsl(var(--primary-foreground));
		}

		&:focus {
			outline: none !important;
			box-shadow: none !important;
			border: 0 !important;
		}

		&:-webkit-autofill,
		&:-webkit-autofill:hover,
		&:-webkit-autofill:focus {
			-webkit-text-fill-color: hsl(var(--foreground));
			-webkit-box-shadow: 0 0 0 1000px hsl(var(--background)) inset;
			transition: background-color 9999s ease-in-out 0s;
		}
	}
`;

const Input = (props) => (
	<StyledInput {...props} />
);

// When focus moves from the input into the open menu, react-select puts it
// straight back on the input from its own blur handler with a bare
// `this.inputRef.focus()` — no null check. React clears that ref as the input is
// torn down, so a blur delivered mid-teardown reaches the handler with nothing
// to focus and throws "Cannot read properties of null (reading 'focus')",
// taking the page down with it.
//
// The handler can't be reached through `components`: with `isSearchable={false}`
// react-select renders its internal DummyInput, which is not overridable. It is
// an instance field though, so wrapping it on the instance covers every render
// path. Attach the returned ref to the Select. See PLATFORM-4058.
export const useSafeInputBlur = () => {
	const selectRef = React.useRef(null);

	React.useEffect(() => {
		const select = selectRef.current;
		if(!select || typeof select.onInputBlur !== 'function') {
			return undefined;
		}

		const onInputBlur = select.onInputBlur;
		select.onInputBlur = (event) => {
			// Nothing left to re-focus, so react-select would only throw. Bailing
			// matches what it does anyway once the input is back in hand.
			if(!select.inputRef) {
				return;
			}
			onInputBlur.call(select, event);
		};

		return () => {
			select.onInputBlur = onInputBlur;
		};
	}, []);

	return selectRef;
};

const SelectMenu = ({ id, inputId, 'data-testid': dataTestId, ...props }) => {
	// `react-select` v5 only applies its `id` prop to the SelectContainer in
	// some setups, and arbitrary attrs like `data-testid` are dropped from
	// the rendered DOM. Wrap the select in our own div carrying the id +
	// data-testid so cypress (`cy.get('#x')`), testing-library
	// (`getByTestId('x')`), and assertions like `.should('contain', menu text)`
	// all resolve against the visible control. Cypress's `.type()` still
	// falls through to the focused inner input because react-select forwards
	// keyboard events from the wrapper's bubble path.
	// Only forward `inputId` to react-select when explicitly supplied. When it
	// is, the inner input carries that id, so the wrapper must NOT fall back to
	// it — that would create duplicate ids in the DOM (breaking `getById` /
	// `cy.get('#x')`, which expect a single match; the inner input is the right
	// target for click/type anyway).
	// Mirror inputId → data-testid (callers that supply `inputId` use the
	// same value for their `getByTestId` queries). Don't mirror plain `id`,
	// since a sibling element (e.g. an adjacent <label>) may share that id
	// as its own data-testid and collide.
	const wrapperId = id || dataTestId || undefined;
	const wrapperTestId = dataTestId || inputId || undefined;
	// react-select stamps `px-react-select--is-disabled` on its own container,
	// which is now a child of our wrapper. Mirror it onto the wrapper so
	// `cy.get('#x').should('have.class', 'px-react-select--is-disabled')`
	// (which targets the id, i.e. the wrapper) still passes.
	const wrapperClassName = props.isDisabled ? 'px-react-select--is-disabled' : undefined;
	return (
		<div id={wrapperId} data-testid={wrapperTestId} className={wrapperClassName}>
			<Select
				ref={useSafeInputBlur()}
				inputId={inputId || undefined}
				classNamePrefix="px-react-select"
				styles={customStyles}
				components={{ DropdownIndicator: StyledDropdownIndicator, Input, ...props.components }}
				{...props}
			/>
		</div>
	);
};

SelectMenu.propTypes = {
	id: PropTypes.string,
	inputId: PropTypes.string,
	isDisabled: PropTypes.bool,
	components: PropTypes.object,
	'data-testid': PropTypes.string
};

export const AsyncSelectMenu = ({ styles, ...props }) => (
	<AsyncSelect
		ref={useSafeInputBlur()}
		className={props.className || ''}
		classNamePrefix="px-react-select"
		menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
		menuPosition="fixed"
		styles={styles || customStyles}
		components={{ DropdownIndicator: StyledDropdownIndicator, Input, ...props.components }}
		{...props}
	/>
);

AsyncSelectMenu.propTypes = {
	styles: PropTypes.object,
	className: PropTypes.string,
	components: PropTypes.object
};

export default SelectMenu;
